import { useEffect, useRef } from "react"
import type { ReactNode } from "react"
import { cn } from "@/lib/cn"

// Responsive overlay primitive — positionally-aware (`side` chooses which
// edge it docks to) and built on the native `<dialog>` element so it
// gets focus trap + ESC-to-close for free. Use this for any operator-
// facing surface that needs to open from chrome (the Tickets/Show meta
// panel, the customer-context drawer, the Forge / Opus side panels, the
// mobile nav drawer) — adopting `<Sheet>` instead of `<Dialog>` keeps
// responsive behavior consistent across pages.
//
// API:
//   <Sheet open={isOpen} onClose={() => setOpen(false)} side="bottom">
//     <SheetHeader title="Details" onClose={() => setOpen(false)} />
//     <SheetBody>...</SheetBody>
//     <SheetFooter>...</SheetFooter>
//   </Sheet>
//
// `side` chooses the docking edge:
//   bottom (default): mobile bottom-sheet (slide up, rounded top, max
//                     85vh), desktop falls back to a centered modal (the
//                     existing Dialog look). Best for content overlays
//                     triggered from page chrome (sheet from the bottom
//                     of the screen).
//   right:            full-height drawer pinned to the right edge at all
//                     widths. Use for contextual side panels (ticket
//                     meta, Forge drawer).
//   left:             full-height drawer pinned to the left edge at all
//                     widths. Use for navigation (mobile nav drawer).
//   modal:            centered modal at all widths. Use when content
//                     isn't tied to an edge (forms, confirmations).
type SheetSide = "bottom" | "right" | "left" | "modal"

type Props = {
  open: boolean
  onClose: () => void
  children: ReactNode
  className?: string
  side?: SheetSide
  // Tailwind width class. Used for the desktop modal cap on `side="bottom"`
  // (default `lg:max-w-2xl`) and the rail width on `side="left/right"`
  // (default `max-w-md`). Pass `lg:max-w-[1100px]` on bottom for wide
  // designer drawers, `max-w-lg` for medium side rails, etc.
  width?: string
}

export function Sheet({
  open,
  onClose,
  children,
  className,
  side = "bottom",
  width,
}: Props) {
  const ref = useRef<HTMLDialogElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])

  // Resolve the width class per side. Bottom variant only applies width
  // at lg:+ (it's edge-to-edge on mobile); side rails apply at all
  // widths. Defaults are tuned for the most common caller; widening
  // designer drawers / shrinking confirmations is a one-prop change.
  const widthClass = width ?? (side === "bottom" ? "lg:max-w-2xl" : "max-w-md")
  const sideClass = SIDE[side]

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        // close on backdrop click — same trick as <Dialog>: the dialog
        // element itself receives the click only if it lands outside the
        // inner content (which stops propagation by default for child
        // clicks).
        if (e.target === ref.current) onClose()
      }}
      className={cn(
        "thin-scrollbar p-0 text-app-text outline-none backdrop:bg-black/60",
        sideClass,
        widthClass,
        className,
      )}
    >
      {children}
    </dialog>
  )
}

const SIDE: Record<SheetSide, string> = {
  // Bottom: mobile slide-up sheet (rounded top, 85vh max). Desktop falls
  // back to a centered modal — `lg:m-auto` re-centers the dialog (browser
  // default for `<dialog>` without explicit positioning). Caller-supplied
  // `width` controls the desktop max-width cap (default `lg:max-w-2xl`).
  bottom:
    "mobile-bottom-sheet fixed inset-x-0 bottom-0 m-0 mt-auto w-full max-h-[85vh] overflow-y-auto " +
    "lg:static lg:m-auto lg:rounded-card lg:border lg:border-border lg:bg-app-panel lg:max-h-[85vh]",

  // Right drawer — full-height, pinned to the right edge at every width.
  // `inset-y-0` sticks it to top + bottom; `right-0 left-auto` overrides
  // the centering the browser applies to <dialog> by default.
  right:
    "fixed inset-y-0 right-0 left-auto m-0 h-screen max-h-screen w-full overflow-y-auto " +
    "border-l border-y-0 border-r-0 border-border bg-app-panel",

  // Left drawer — mirror image of right. Used by the mobile nav drawer.
  left:
    "fixed inset-y-0 left-0 right-auto m-0 h-screen max-h-screen w-[88vw] overflow-y-auto " +
    "border-r border-y-0 border-l-0 border-border bg-app-panel",

  // Modal — centered at all widths. Same look as <Dialog>.
  modal:
    "m-auto rounded-card border border-border bg-app-panel max-h-[85vh] overflow-y-auto",
}

// Header with title + dismiss button. Pass the same `onClose` you handed
// to <Sheet> so the X icon dismisses the sheet from inside its own
// content (mobile users can't always reach the backdrop). Sticky so it
// stays visible when the body scrolls.
export function SheetHeader({
  title,
  subtitle,
  onClose,
}: {
  title: string
  subtitle?: ReactNode
  onClose?: () => void
}) {
  return (
    <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-app-panel/95 px-5 py-4 backdrop-blur">
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-lg font-semibold text-app-text">{title}</h2>
        {subtitle && <div className="mt-1 text-sm text-app-muted">{subtitle}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-border bg-white/[0.03] text-lg text-app-muted transition hover:bg-white/10 hover:text-app-text"
        >
          ×
        </button>
      )}
    </header>
  )
}

export function SheetBody({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn("space-y-4 px-5 py-5", className)}>{children}</div>
}

export function SheetFooter({ children }: { children: ReactNode }) {
  return (
    <footer className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-app-panel/95 px-5 py-4 backdrop-blur">
      {children}
    </footer>
  )
}
